import {
  MenuImageItem,
  useMenuImageItemsLoader,
} from '@/experiences/sales-portal/blocs/loaders/use-menu-image-items-loader';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { uploadMenuImage } from '@/experiences/sales-portal/platform/uploads/menu-image-upload';
import { useCreateOrderGuideFromMenuItemsMutator } from '@/experiences/sales-portal/blocs/mutators/use-create-order-guide-from-menu-items-mutator';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';

/**
 * Steps of the upload-photo order guide flow:
 *
 *   select → uploading → scanning → review → generating → (navigates away)
 *
 * `uploading` and `scanning` are distinct so the UI can narrate the wait; on
 * an upload/extraction failure the flow falls back to `select`, and on a
 * generation failure it moves to `generationFailed`, from which the user can
 * retry generation.
 */
export enum CreateOrderGuideFromImageStep {
  Generating = 'generating',
  GenerationFailed = 'generationFailed',
  Review = 'review',
  Scanning = 'scanning',
  Select = 'select',
  Uploading = 'uploading',
}

interface CreateOrderGuideFromImageParams {
  restaurantUUID: string;
  supplierUUID: string;
}

// Identity for de-duping picked files: two File objects are never reference
// equal, so the same photo (by name, size, and modified time) is only added once.
export const fileKey = (file: File) =>
  `${file.name}:${file.size}:${file.lastModified}`;

interface CreateOrderGuideFromImageResult {
  addImages: (images: File[]) => void;
  back: () => void;
  generate: () => Promise<boolean>;
  items: MenuImageItem[];
  processImages: () => Promise<void>;
  removeImage: (index: number) => void;
  reset: () => void;
  selectedImages: File[];
  step: CreateOrderGuideFromImageStep;
}

/**
 * Exposes the current step of the upload-photo order guide flow plus the actions
 * that advance it; the consumer renders off `step` and wires the actions to UI.
 * This hook owns the sequencing (upload → extract → review → generate) and the
 * selected-file and extracted-item state, delegating each step to a focused
 * bloc (image upload, item extraction, generation).
 *
 * @param params.restaurantUUID - The UUID of the restaurant to generate for
 * @param params.supplierUUID - The UUID of the supplier
 * @returns The current step, the selected images, the extracted items, and the
 * actions (`addImages`, `removeImage`, `back`, `processImages`, `generate`,
 * `reset`) that advance the flow
 *
 * @example
 * ```tsx
 * const { addImages, processImages, items, generate, step } =
 *   useCreateOrderGuideFromImage({ restaurantUUID, supplierUUID });
 *
 * addImages(pickedImages);
 * await processImages();
 * // `step` is now 'review' and `items` holds the extracted menu items
 * if (await generate()) {
 *   // navigated to the new order guide
 * }
 * ```
 */
export const useCreateOrderGuideFromImage = ({
  restaurantUUID,
  supplierUUID,
}: CreateOrderGuideFromImageParams): CreateOrderGuideFromImageResult => {
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const { showErrorToast } = useExperienceToasts();
  const { loadMenuItems } = useMenuImageItemsLoader();
  const { createOrderGuideFromMenuItems } =
    useCreateOrderGuideFromMenuItemsMutator({
      restaurantUUID,
      supplierUUID,
    });

  const [step, setStep] = useState<CreateOrderGuideFromImageStep>(
    CreateOrderGuideFromImageStep.Select,
  );
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [items, setItems] = useState<MenuImageItem[]>([]);

  const addImages = (images: File[]) => {
    setSelectedImages((previous) => {
      const seen = new Set(previous.map(fileKey));
      const deduped = images.filter((image) => {
        const key = fileKey(image);
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
      return [...previous, ...deduped];
    });
  };

  const removeImage = (index: number) => {
    setSelectedImages((previous) =>
      previous.filter((_, imageIndex) => imageIndex !== index),
    );
  };

  const reset = () => {
    setStep(CreateOrderGuideFromImageStep.Select);
    setSelectedImages([]);
    setItems([]);
  };

  const back = () => {
    setItems([]);
    setStep(CreateOrderGuideFromImageStep.Select);
  };

  const processImages = async () => {
    if (selectedImages.length === 0) {
      return;
    }

    try {
      setStep(CreateOrderGuideFromImageStep.Uploading);
      const imageUrls = await Promise.all(selectedImages.map(uploadMenuImage));
      setStep(CreateOrderGuideFromImageStep.Scanning);
      setItems(await loadMenuItems(imageUrls));
      setStep(CreateOrderGuideFromImageStep.Review);
    } catch {
      showErrorToast();
      setStep(CreateOrderGuideFromImageStep.Select);
    }
  };

  const generate = async (): Promise<boolean> => {
    setStep(CreateOrderGuideFromImageStep.Generating);
    const createdOrderGuide = await createOrderGuideFromMenuItems(items);
    if (!createdOrderGuide) {
      setStep(CreateOrderGuideFromImageStep.GenerationFailed);
      return false;
    }

    navigateToSalesPortalRoute(navigation, SALES_PORTAL_ROUTES.OrderGuides, {
      orderGuideUUID: createdOrderGuide.orderGuideCollectionUUID,
      partitionUUID: createdOrderGuide.orderGuidePartitionUUID,
      restaurantUUID,
      supplierUUID,
    });

    return true;
  };

  return {
    addImages,
    back,
    generate,
    items,
    processImages,
    removeImage,
    reset,
    selectedImages,
    step,
  };
};
